import { Box, Typography } from '@mui/material';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Logger } from '../../logger';
import { GetOrganizationRequests } from '../../services/user/types';
import TableComponent from './TableComponent';
import TabsComponent from './TabsComponent';
import { Data, Order, Status, TabPanelProps } from './types';

export const descendingComparator = <T,>(a: T, b: T, orderBy: keyof T): number => {
    const aValue = a[orderBy] as string | number | undefined;
    const bValue = b[orderBy] as string | number | undefined;
    if (aValue === undefined || bValue === undefined) return 0;
    if (bValue < aValue) return -1;
    if (bValue > aValue) return 1;
    return 0;
};

export const CustomTabPanel: React.FC<TabPanelProps> = ({ children, value, index, ...other }) => (
    <div
        role="tabpanel"
        hidden={value !== index}
        id={`simple-tabpanel-${index}`}
        aria-labelledby={`simple-tab-${index}`}
        {...other}
    >
        {value === index && <Box sx={{ paddingTop: 2 }}>{children}</Box>}
    </div>
);

const getComparator =
    (order: Order, orderBy: keyof Data): ((a: Data, b: Data) => number) =>
    (a: Data, b: Data) => {
        // Safely handle optional properties
        const aValue = a[orderBy] ?? '';
        const bValue = b[orderBy] ?? '';
        if (aValue < bValue) return order === 'asc' ? -1 : 1;
        if (aValue > bValue) return order === 'asc' ? 1 : -1;
        return 0;
    };

type AdminDashboardTableProps = {
    tableData: GetOrganizationRequests[];
    onRefresh?: () => void;
};

export enum TabIndex {
    REQUESTED = 0,
    APPROVED = 1,
    REJECTED = 2,
}

export const AdminDashboardTable: React.FC<AdminDashboardTableProps> = (props) => {
    const { tableData = [], onRefresh } = props;
    const { t } = useTranslation();
    const [order, setOrder] = useState<Order>('desc');
    const [orderBy, setOrderBy] = useState<keyof Data>('requestedDate');
    const [selected, setSelected] = useState<number[]>([]);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [value, setValue] = useState(0);

    const handleRequestSort = (event: React.MouseEvent<unknown>, property: keyof Data): void => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>): void => {
        if (event.target.checked) {
            const newSelected = tableData.map((n) => Number(n.organizationId));
            setSelected(newSelected);
            return;
        }
        setSelected([]);
    };

    const handleChangePage = (_: unknown, newPage: number): void => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>): void => {
        const newRowsPerPage = parseInt(event.target.value, 10);
        setRowsPerPage(newRowsPerPage);
        setPage(0);
    };

    const handleChange = (_: React.SyntheticEvent, newValue: number): void => {
        setValue(newValue);
        setPage(0); // Reset pagination when tab changes

        // Set default sorting based on tab
        switch (newValue as TabIndex) {
            case TabIndex.REQUESTED:
                setOrderBy('requestedDate');
                setOrder('desc'); // Newest first
                break;
            case TabIndex.APPROVED:
            case TabIndex.REJECTED:
                setOrderBy('reviewedDate');
                setOrder('desc'); // Newest first
                break;
            default:
                Logger.error(
                    `Unexpected tab index: ${newValue}. Organizations should be in REQUESTED, APPROVED, or REJECTED state.`
                );
                setOrderBy('requestedDate');
                setOrder('desc');
                break;
        }
    };
    const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - tableData.length) : 0;

    const getFilteredRows = (): GetOrganizationRequests[] => {
        if (!Array.isArray(tableData)) return [];
        switch (value as TabIndex) {
            case TabIndex.REQUESTED:
                return tableData.filter((row) => (row.status as Status) === Status.REQUESTED);
            case TabIndex.APPROVED:
                return tableData.filter(
                    (row) => (row.status as Status) === Status.APPROVED || (row.status as Status) === Status.CLAIMED
                );
            case TabIndex.REJECTED:
                return tableData.filter((row) => (row.status as Status) === Status.REJECTED);
            default:
                return [];
        }
    };

    const visibleRows = getFilteredRows();

    return (
        <Box>
            <Box>
                <TabsComponent
                    value={value}
                    onChange={handleChange}
                    labels={[
                        t('ADMIN_DASHBOARD.NEW_REQUESTS'),
                        t('ADMIN_DASHBOARD.APPROVED_REQUESTS'),
                        t('ADMIN_DASHBOARD.REJECTED_REQUESTS'),
                    ]}
                />

                {[TabIndex.REQUESTED, TabIndex.APPROVED, TabIndex.REJECTED].map((index) => (
                    <CustomTabPanel key={index} value={value} index={index}>
                        {getFilteredRows().length > 0 ? (
                            <TableComponent
                                rows={visibleRows}
                                order={order}
                                orderBy={orderBy}
                                onRequestSort={handleRequestSort}
                                onSelectAllClick={handleSelectAllClick}
                                numSelected={selected.length}
                                rowCount={tableData.length}
                                page={page}
                                rowsPerPage={rowsPerPage}
                                emptyRows={emptyRows}
                                onPageChange={handleChangePage}
                                onRowsPerPageChange={handleChangeRowsPerPage}
                                onStatusChange={onRefresh}
                                comparator={getComparator(order, orderBy)}
                            />
                        ) : (
                            <Typography>{t('ADMIN_DASHBOARD.NO_REQUESTS')}</Typography>
                        )}
                    </CustomTabPanel>
                ))}
            </Box>
        </Box>
    );
};
